import { useContext, useEffect, useState } from 'react';
import {
  Backdrop,
  Box,
  CircularProgress,
  Fab,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Delete,
  Settings,
} from '@mui/icons-material';
import {
  Appointments,
  AppointmentTooltip,
  DateNavigator,
  DayView,
  Resources,
  Scheduler,
  TodayButton,
  Toolbar,
  WeekView,
} from '@devexpress/dx-react-scheduler-material-ui';
import { ViewState } from '@devexpress/dx-react-scheduler';
import { useNavigate } from 'react-router-dom';
import { blue, green, grey, orange } from '@mui/material/colors';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Controller, useForm } from 'react-hook-form';
import { AgendaContext } from 'contexts/AgendaContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { toastWarning } from 'utils/toast';
import { encryptURL } from 'utils/functions';
import { useDrawer, useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Header from 'components/Header';
import Button from 'components/Button';
import Input from 'components/Input';
import localeText from 'utils/localeText';
import styles from './styles';
import moment from 'moment';

const Agenda = () => {
  const navigate = useNavigate();
  const storedValues = JSON.parse(localStorage.getItem('E-SOLUTION:SCHEDULE'));
  const { openDrawer, closeDrawer } = useDrawer();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { deleteGrid, deleteLoading } = useContext(GridContext);
  const { getLoading, getAgendas, postAgendas } = useContext(AgendaContext);
  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM-DD'));
  const [agendas, setAgendas] = useState([]);
  const defaultValues = storedValues || {
    currentViewName: 'Day',
    startDayHour: 8,
    endDayHour: 18,
    cellDuration: 30,
  };
  const { control, watch, handleSubmit } = useForm({ defaultValues });
  const formValues = watch();
  const rotina = 'Tarefa';
  const hours = [...Array(25).keys()].map((value) => ({
    label: (value < 10 ? '0' + value : value) + ':00',
    value,
  }));
  const types = [
    { label: 'AVULSO', value: 'AVULSO' },
    { label: 'RECORRENTE', value: 'RECORRENTE' },
    { label: 'PEDIDO', value: 'PEDIDO' },
  ];
  const unidades = [
    // { label: 'MINUTO', value: 'minutes' },
    // { label: 'HORA', value: 'hours' },
    { label: 'DIA', value: 'days' },
    { label: 'SEMANA', value: 'weeks' },
    { label: 'MÊS', value: 'months' },
    { label: 'ANO', value: 'years' },
  ];
  const semana = [
    { value: 0, label: 'D' },
    { value: 1, label: 'S' },
    { value: 2, label: 'T' },
    { value: 3, label: 'Q' },
    { value: 4, label: 'Q' },
    { value: 5, label: 'S' },
    { value: 6, label: 'S' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const normalize = (date) => moment(date).seconds(0).milliseconds(0);

  const loadData = (date = currentDate) => {
    const params = {
      inicio: moment(date).startOf('month').format('YYYY-MM-DD'),
      fim: moment(date).endOf('month').format('YYYY-MM-DD'),
    };
    const cb = (res) => {
      setAgendas(
        res?.map((a) => ({
          ...a,
          ...(Boolean(a?.datahora) && {
            startDate: a?.datahora && moment(a?.datahora).toDate(),
          }),
          ...(Boolean(a?.conclusao) && {
            endDate: a?.conclusao && moment(a?.conclusao).toDate(),
          }),
          title: a?.assunto,
        }))
      );
      closeDrawer();
      closeModal();
    };

    getAgendas({ params, cb });
  };

  const handleConfig = (values) => {
    localStorage.setItem('E-SOLUTION:SCHEDULE', JSON.stringify(values));
    closeDrawer();
  };

  const handleSubmitAgenda = ({ agendas, tipo, termino, unidade, ...rest }) => {
    if (!tipo) {
      return toastWarning('Preencha o tipo da agenda.');
    }
    if (tipo === 'PEDIDO') {
      if (!agendas?.length) {
        return toastWarning('Adicione pelo menos uma agenda.');
      }
      closeDrawer();
      const hash = encryptURL(agendas);
      navigate(`/app/Pedidos/Gerar/${hash}`);
    } else {
      const data = {
        id: rest?.id,
        useremit_id: rest?.useremit_id,
        userdest_id: rest?.userdest_id,
        datahora: normalize(rest.datahora).format('YYYY-MM-DD HH:mm:ss'),
        conclusao: normalize(rest.conclusao).format('YYYY-MM-DD HH:mm:ss'),
        assunto: rest?.assunto,
        tarefa: rest?.tarefa,
        ...(tipo === 'RECORRENTE' && {
          intervalo: rest?.intervalo,
          unidade,
          ...(unidade === 'weeks' && { dias_semana: rest?.dias_semana }),
          ...(termino === 'data'
            ? { dttermino: rest?.dttermino }
            : { quantidade: rest?.quantidade }),
        }),
      };
      postAgendas({ data, cb: loadData });
    }
  };

  const handleDelete = (agenda) => {
    const params = { rotina, id: agenda?.id };
    openModal(
      <DeleteModal onSubmit={() => deleteGrid({ params, cb: loadData })} />
    );
  };

  const handleEdit = (agenda) => {
    openDrawer(<DrawerAgenda agenda={agenda} />, 'Editar Agendamento');
  };

  const Line = ({ value, label }) =>
    Boolean(value) && (
      <Box display="flex" alignItems="center" textAlign="start">
        <Typography flex={1} variant="subtitle2">
          {label}:
        </Typography>
        <Typography flex={2} variant="body2">
          {value}
        </Typography>
      </Box>
    );

  const CustomToolbar = ({ children, ...restProps }) => (
    <Toolbar.Root {...restProps}>
      {children}
      <IconButton
        color="primary"
        onClick={() => openDrawer(<DrawerConfig />, 'Configurações')}
      >
        <Settings />
      </IconButton>
    </Toolbar.Root>
  );

  const CustomNavigationButton = ({ onClick, type, ...restProps }) => (
    <IconButton {...restProps} onClick={onClick} size="medium">
      {type === 'back' ? <ChevronLeft /> : <ChevronRight />}
    </IconButton>
  );

  const CustomTodayButton = ({ getMessage, setCurrentDate }) => (
    <Button variant="outlined" onClick={() => setCurrentDate(new Date())}>
      {getMessage('today')}
    </Button>
  );

  const CustomAppointmentContent = ({ data, ...restProps }) => {
    const inicio =
      moment(data?.datahora).isValid() &&
      moment(data?.datahora).format('HH:mm');
    const fim =
      moment(data?.conclusao).isValid() &&
      moment(data?.conclusao).format('HH:mm');
    const infoItems = [
      data?.title,
      data?.Documento?.documento && `Pd: ${data?.Documento?.documento}`,
      data?.Item && data?.Item?.descricao,
      data?.Item &&
        `${data?.Item?.qtgerada}/${data?.Item?.quantidade} ${data?.Item?.um}`,
      data?.Documento?.Entidade?.nome && `${data?.Documento?.Entidade?.nome}`,
      ,
    ].filter(Boolean);

    return (
      <Appointments.AppointmentContent {...restProps} data={data}>
        <Box flex={1} display="flex" flexDirection="column">
          <Box gap={1} flex={1} display="flex" flexDirection="row">
            {infoItems?.map((text, index) => (
              <>
                <strong key={index}>{text}</strong>
                {index < infoItems.length - 1 && <span>•</span>}
              </>
            ))}
            {restProps?.durationType === 'short' && (
              <>
                <span>•</span>
                <strong>
                  {inicio}
                  {fim ? ` - ${fim}` : ''}
                </strong>
              </>
            )}
          </Box>
          {restProps?.durationType !== 'short' && (
            <strong>
              {inicio}
              {fim ? ` - ${fim}` : ''}
            </strong>
          )}
        </Box>
      </Appointments.AppointmentContent>
    );
  };

  const CustomHeaderComponent = ({ ...restProps }) => (
    <AppointmentTooltip.Header
      {...restProps}
      onDeleteButtonClick={() => handleDelete(restProps?.appointmentData)}
      onOpenButtonClick={() => handleEdit(restProps?.appointmentData)}
    />
  );

  const CustomContentComponent = ({ appointmentData: data, ...restProps }) => (
    <AppointmentTooltip.Content {...restProps} appointmentData={data}>
      <Box mt={2} gap={1} px="20px" display="flex" flexDirection="column">
        <Line label="Cliente" value={data?.Documento?.Entidade?.nome} />
        <Line label="Produto" value={data?.Item?.descricao} />
        <Line
          label="Quantidade"
          value={
            data?.Item?.quantidade &&
            `${data?.Item?.qtgerada}/${data?.Item?.quantidade} ${
              data?.Item?.um || ''
            }`
          }
        />
        {Boolean(data?.documento_id) && (
          <Button
            onClick={() => navigate(`/app/Documento/${data?.documento_id}`)}
          >
            Documento
            {data?.Documento?.documento
              ? ` Nº ${data?.Documento?.documento}`
              : ''}
          </Button>
        )}
      </Box>
    </AppointmentTooltip.Content>
  );

  const CustomLayoutComponent = (props) => {
    return (
      <AppointmentTooltip.Layout
        {...props}
        showDeleteButton={props?.appointmentMeta?.data?.exclui === 'SIM'}
      />
    );
  };

  const CustomTimeTableCell = (props) => {
    const { startDate, endDate } = props;
    return (
      <DayView.TimeTableCell
        {...props}
        onClick={() => {
          openDrawer(
            <DrawerAgenda
              agenda={{
                datahora: normalize(startDate).format('YYYY-MM-DD HH:mm:ss'),
                conclusao: normalize(endDate).format('YYYY-MM-DD HH:mm:ss'),
              }}
            />,
            'Novo Agendamento'
          );
        }}
      />
    );
  };

  const CustomWeekTimeTableCell = (props) => {
    const { startDate, endDate } = props;
    return (
      <WeekView.TimeTableCell
        {...props}
        onClick={() => {
          openDrawer(
            <DrawerAgenda
              agenda={{
                datahora: normalize(startDate).format('YYYY-MM-DD HH:mm:ss'),
                conclusao: normalize(endDate).format('YYYY-MM-DD HH:mm:ss'),
              }}
            />,
            'Novo Agendamento'
          );
        }}
      />
    );
  };

  const DrawerConfig = () => (
    <Box mx={2}>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel focused={false}>
              Modo de exibição do calendário
            </FormLabel>
            <Controller
              name="currentViewName"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="Day"
                    label="Dia"
                    sx={styles?.radio}
                    control={<Radio />}
                  />
                  <FormControlLabel
                    value="Week"
                    label="Semana"
                    sx={styles?.radio}
                    control={<Radio />}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel focused={false}>Intervalo entre horários</FormLabel>
            <Controller
              name="cellDuration"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value={15}
                    label="15 minutos"
                    sx={styles?.radio}
                    control={<Radio />}
                  />
                  <FormControlLabel
                    value={30}
                    label="30 minutos"
                    sx={styles?.radio}
                    control={<Radio />}
                  />
                  <FormControlLabel
                    value={60}
                    label="1 hora"
                    sx={styles?.radio}
                    control={<Radio />}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Mostrar agenda a partir das</InputLabel>
            <Controller
              name="startDayHour"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  {...styles?.select}
                  label="Mostrar agenda a partir das"
                >
                  {hours?.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Ocultar agenda após as</InputLabel>
            <Controller
              name="endDayHour"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  {...styles?.select}
                  label="Ocultar agenda após as"
                >
                  {hours?.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button variant="contained" onClick={handleSubmit(handleConfig)}>
            Salvar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  const DrawerAgenda = ({ agenda }) => {
    const defaultValues = {
      id: agenda?.id,
      useremit_id: agenda?.useremit_id || user?.id,
      userdest_id: agenda?.userdest_id || user?.id,
      tipo: 'AVULSO',
      datahora: normalize(
        agenda?.datahora ||
          moment(currentDate).set({
            hour: moment().hour(),
            minute: moment().minute(),
          })
      ).format('YYYY-MM-DD HH:mm:ss'),
      conclusao: normalize(
        agenda?.conclusao ||
          moment(currentDate).set({
            hour: moment().hour(),
            minute: moment().minute(),
          })
      ).format('YYYY-MM-DD HH:mm:ss'),
      assunto: agenda?.assunto || '',
      tarefa: agenda?.tarefa || '',
      agendas: [],
      intervalo: 1,
      unidade: 'weeks',
      dias_semana: [moment(agenda?.datahora).day()],
      termino: 'data',
      quantidade: 1,
      dttermino: null,
    };
    const { control, handleSubmit, watch, setValue } = useForm({
      defaultValues,
    });
    const { postLoading } = useContext(AgendaContext);
    const agendas = watch('agendas');
    const dias_semana = watch('dias_semana');
    const tipo = watch('tipo');
    const unidade = watch('unidade');
    const termino = watch('termino');
    const showAgendas = tipo === 'PEDIDO';
    const showRecorrencias = tipo === 'RECORRENTE';

    const handleAddAgenda = ({ agendas, ...rest }) => {
      if (!rest?.assunto) {
        return toastWarning('Preencha o título da agenda.');
      }
      if (!rest?.datahora) {
        return toastWarning('Preencha o início da agenda.');
      }
      setValue('agendas', [
        ...agendas,
        {
          ...rest,
          datahora: normalize(rest.datahora).format('YYYY-MM-DD HH:mm:ss'),
          conclusao: normalize(rest.conclusao).format('YYYY-MM-DD HH:mm:ss'),
          index: agendas?.length + 1,
        },
      ]);
    };

    return (
      <Box
        px={2}
        height="90vh"
        display="flex"
        alignItems="center"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Input name="assunto" control={control} label="Título" autoFocus />
          </Grid>
          <Grid item xs={12}>
            <Dropdown
              name="tipo"
              control={control}
              label="Tipo de Agendamento"
              options={types?.filter((f) =>
                Boolean(agenda?.id) ? f?.value !== 'RECORRENTE' : true
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <InputMask
              type="datetime"
              name="datahora"
              control={control}
              label="Data / Hora Início"
            />
          </Grid>
          <Grid item xs={12}>
            <InputMask
              type="datetime"
              name="conclusao"
              control={control}
              label="Data / Hora Fim"
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              rows={5}
              multiline
              name="tarefa"
              control={control}
              label="Observação"
            />
          </Grid>
          {showAgendas && (
            <>
              <Grid item xs={12} textAlign="center">
                <Button
                  variant="outlined"
                  loading={postLoading}
                  onClick={handleSubmit(handleAddAgenda)}
                >
                  Adicionar
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" mt={2}>
                  Agendamentos do Pedido
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <DataGrid
                  rows={agendas}
                  columns={[
                    {
                      field: 'actions',
                      type: 'actions',
                      width: 80,
                      headerName: 'Ações',
                      getActions: ({ row }) => {
                        return [
                          <GridActionsCellItem
                            icon={<Delete />}
                            label="Excluir"
                            onClick={() =>
                              setValue(
                                'agendas',
                                agendas.filter((f) => f?.index !== row?.index)
                              )
                            }
                          />,
                        ];
                      },
                    },
                    {
                      field: 'assunto',
                      headerName: 'Título',
                      minWidth: 150,
                      flex: 1,
                      sortable: false,
                    },
                    {
                      field: 'datahora',
                      headerName: 'Início',
                      width: 100,
                      sortable: false,
                      valueFormatter: ({ value }) =>
                        value && moment(value).format('DD/MM/YYYY [às] HH:mm'),
                    },
                    {
                      field: 'conclusao',
                      headerName: 'Fim',
                      width: 100,
                      sortable: false,
                      valueFormatter: ({ value }) =>
                        value && moment(value).format('DD/MM/YYYY [às] HH:mm'),
                    },
                  ]}
                  hideFooter
                  autoHeight
                  density="compact"
                  getRowId={(row) => row?.index}
                  getRowHeight={() => 'auto'}
                  disableSelectionOnClick
                  disableColumnMenu
                  showCellRightBorder
                  showColumnRightBorder
                  localeText={localeText}
                />
              </Grid>
            </>
          )}
          {showRecorrencias && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" mt={2}>
                  Recorrências
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography>Repetir a cada:</Typography>
                  <Box flex={1}>
                    <InputMask name="intervalo" control={control} />
                  </Box>
                  <Box flex={2}>
                    <Dropdown
                      name="unidade"
                      control={control}
                      options={unidades}
                    />
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} display={unidade !== 'weeks' && 'none'}>
                <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                  <Typography>Repetir:</Typography>
                  <Stack direction="row" spacing={1}>
                    {semana?.map((dia) => {
                      const selected = dias_semana?.includes(dia?.value);
                      return (
                        <ToggleButton
                          key={dia?.value}
                          value={dia?.value}
                          selected={selected}
                          sx={styles?.circle}
                          onChange={() => {
                            let newValue;
                            if (selected) {
                              newValue = dias_semana?.filter(
                                (d) => d !== dia?.value
                              );
                            } else {
                              newValue = [...(dias_semana || []), dia?.value];
                            }
                            setValue('dias_semana', newValue);
                          }}
                        >
                          {dia?.label}
                        </ToggleButton>
                      );
                    })}
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <RadioGroup
                  value={termino}
                  onChange={(e) => setValue('termino', e.target.value)}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                    <FormControlLabel
                      value="data"
                      control={<Radio />}
                      label="Termina em:"
                      sx={termino !== 'data' && styles?.textDisabled}
                    />
                    <Box flex={1}>
                      <InputMask
                        type="date"
                        name="dttermino"
                        control={control}
                        disabled={termino !== 'data'}
                      />
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={2} alignItems="center" mt={2}>
                    <FormControlLabel
                      value="ocorrencias"
                      control={<Radio />}
                      label="Termina após:"
                      sx={termino !== 'ocorrencias' && styles?.textDisabled}
                    />
                    <Box flex={1}>
                      <InputMask
                        name="quantidade"
                        control={control}
                        disabled={termino !== 'ocorrencias'}
                      />
                    </Box>
                    <Typography
                      sx={termino !== 'ocorrencias' && styles?.textDisabled}
                    >
                      ocorrências
                    </Typography>
                  </Stack>
                </RadioGroup>
              </Grid>
            </>
          )}
        </Grid>
        <Button
          variant="contained"
          loading={postLoading}
          onClick={handleSubmit(handleSubmitAgenda)}
        >
          Salvar
        </Button>
      </Box>
    );
  };

  return (
    <Container>
      <Header titulo="Agenda" />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Scheduler data={agendas} locale="pt-BR">
            <ViewState
              currentViewName={formValues?.currentViewName}
              defaultCurrentDate={new Date()}
              onCurrentDateChange={(a) =>
                setCurrentDate((prev) => {
                  const newDate = moment(a).format('YYYY-MM-DD');
                  if (moment(prev).format('MM') !== moment(a).format('MM')) {
                    loadData(newDate);
                  }
                  return newDate;
                })
              }
            />
            <DayView
              displayName="Dia"
              endDayHour={formValues?.endDayHour}
              startDayHour={formValues?.startDayHour}
              cellDuration={formValues?.cellDuration}
              timeTableCellComponent={CustomTimeTableCell}
            />
            <WeekView
              displayName="Semana"
              endDayHour={formValues?.endDayHour}
              startDayHour={formValues?.startDayHour}
              cellDuration={formValues?.cellDuration}
              timeTableCellComponent={CustomWeekTimeTableCell}
            />
            <Toolbar rootComponent={CustomToolbar} />
            <DateNavigator navigationButtonComponent={CustomNavigationButton} />
            {!deleteLoading && (
              <Appointments
                appointmentContentComponent={CustomAppointmentContent}
              />
            )}
            {!deleteLoading && (
              <AppointmentTooltip
                showOpenButton
                showCloseButton
                headerComponent={CustomHeaderComponent}
                layoutComponent={CustomLayoutComponent}
                contentComponent={CustomContentComponent}
              />
            )}
            {!deleteLoading && (
              <Resources
                data={[
                  {
                    fieldName: 'status',
                    title: 'Status',
                    instances: [
                      { id: 'NAO INICIADA', text: 'NÃO INICIADA' },
                      { id: 'DOCUMENTO GERADO', text: 'DOCUMENTO GERADO' },
                      { id: 'EM ANDAMENTO', text: 'EM ANDAMENTO' },
                      { id: 'CONCLUIDA', text: 'CONCLUÍDA' },
                    ],
                  },
                ]}
                mainResourceName="status"
                palette={[orange[700], grey[700], blue[700], green[700]]}
              />
            )}
            <TodayButton
              messages={{ today: 'Hoje' }}
              buttonComponent={CustomTodayButton}
            />
          </Scheduler>
        </Grid>
      </Grid>
      <Fab
        color="primary"
        style={styles?.fab}
        onClick={() => openDrawer(<DrawerAgenda />, 'Novo Agendamento')}
      >
        <Add />
      </Fab>
      <Fade in={getLoading || deleteLoading}>
        <Backdrop open={getLoading || deleteLoading} sx={styles?.loader}>
          <CircularProgress color="inherit" size={48} thickness={4} />
        </Backdrop>
      </Fade>
    </Container>
  );
};

export default Agenda;
